<template>
  <div class="h-full relative">
    <div class="flex flex-col justify-between h-full">
      <template>
        <div class="overflow-x-hidden w-full flex-1">
          <div
            v-if="isLargeScreenLayout"
            ref="timeslotsContainer"
            class="flex flex-col px-5 overflow-y-auto"
          >
            <div
              v-if="hasTimeslots && !isPartnerLoading"
              class="flex flex-col min-h-20"
              :class="{
                'max-h-[calc(24rem+10px)]': showMoreTimeslots,
                'h-[calc(100vh-36rem)]': showMoreTimeslots && showUpsellBanner,
                'h-[calc(100vh-31rem)]': showMoreTimeslots && !showUpsellBanner,
              }"
            >
              <vetster-quote-panel-timeslot-button
                v-for="(timeslot, index) in timeslots"
                :key="index"
                :timeslot="timeslot"
                is-large-screen-layout
              />
              <div
                v-if="showMoreTimeslots"
                class="pb-6"
              />
              <vetster-button
                v-else
                type="button"
                variant="textLink"
                size="tight"
                class="mt-4 mb-5 *:text-neutral-900 hover:opacity-70"
                @click.stop="$emit('show')"
              >
                <span class="inline-flex font-normal text-sm underline underline-offset-2">
                  {{ $t('search.show-more-dates-and-time') }}
                </span>
              </vetster-button>
            </div>
            <div
              v-else-if="!hasTimeslots && (isQuoteLoading || isPartnerLoading)"
              class="mt-4 mb-8 space-y-4 h-[calc(100vh-34rem)] max-h-96"
            >
              <vetster-skeleton-timeslot
                v-for="index in 4"
                :key="index"
              />
            </div>
          </div>
          <div
            v-else
            ref="timeslotsContainer"
            class="grid grid-cols-3 overflow-y-auto gap-5 py-4 px-standard h-full"
            :class="{ hidden: hasNoAvailability }"
          >
            <div class="flex flex-col items-center space-y-2">
              <vetster-quote-panel-timeslot-button
                v-for="(timeslot, index) in morningTimeslots"
                :key="index"
                :timeslot="timeslot"
              />
            </div>
            <div class="flex flex-col items-center space-y-2">
              <vetster-quote-panel-timeslot-button
                v-for="(timeslot, index) in afternoonTimeslots"
                :key="index"
                :timeslot="timeslot"
              />
            </div>
            <div class="flex flex-col items-center space-y-2">
              <vetster-quote-panel-timeslot-button
                v-for="(timeslot, index) in eveningTimeslots"
                :key="index"
                :timeslot="timeslot"
              />
            </div>
          </div>
          <div
            v-if="!isQuoteLoading && hasNoAvailability && !isPartnerLoading"
            class="flex flex-col items-center text-center mt-8 lg:mb-16 lg:mt-4 lg:h-[calc(100vh-36rem)] lg:max-h-96 px-12"
          >
            <img
              :src="getAssetUrl('/images/quote-calendar.svg')"
              class="w-auto h-24 mx-auto mb-4"
              alt="Vetster"
            />
            <p class="text-neutral-900 text-lg lg:text-xl font-bold mb-2">
              {{ $t('quote.dr-isnt-available', { name: formalName, dayOfWeek: getSelectedDate }) }}
            </p>
            <p class="text-neutral-500 text-sm lg:text-base">
              {{ $t('quote.please-choose-a-different-date') }}
            </p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCircleNotch } from '@fortawesome/pro-solid-svg-icons';
import get from 'lodash/get';
import size from 'lodash/size';
import take from 'lodash/take';
import { DateTime } from 'luxon';
import { getCurrentInstance } from 'vue';
import { mapActions, mapGetters } from 'vuex';

import VetsterQuotePanelTimeslotButton from '@/brand-components/GuidedSearch/VetsterQuotePanelTimeslotButton';
import VetsterSkeletonTimeslot from '@/brand-components/VetsterSkeletonTimeslot';
import useCheckoutHelpers from '@/composables/use-checkout-helpers';
import useSmartTime from '@/composables/use-smart-time';
import parsedLocalStorage from '@/helpers/parsedLocalStorage';
import { getAssetUrl } from '@/helpers/url';
import User from '@/models/User';

library.add(faCircleNotch);

export default {
  name: 'VetsterQuotePanel',

  components: {
    VetsterQuotePanelTimeslotButton,
    VetsterSkeletonTimeslot,
  },

  props: {
    defaultDate: {
      type: String,
      default: undefined,
    },
    defaultTimeslot: {
      type: [Object, Array],
      default: () => ({}),
    },
    defaultTimezone: {
      type: String,
      default: undefined,
    },
    isLargeScreenLayout: {
      type: Boolean,
      default: false,
    },
    showMoreTimeslots: {
      type: Boolean,
      default: false,
    },
    showUpsellBanner: {
      type: Boolean,
      default: false,
    },
    partner: {
      type: Object,
      default: () => ({}),
    },
  },

  setup() {
    const root = getCurrentInstance().proxy.$root;
    const { getBookingUrl } = useCheckoutHelpers(root);
    const { validDateFrom } = useSmartTime();

    return {
      getBookingUrl,
      validDateFrom,
    };
  },

  computed: {
    ...mapGetters(['hasUser']),

    ...mapGetters('quote', [
      'getQuote',
      'getAvailability',
      'getInputs',
      'getSelectedDate',
      'hasQuote',
      'hasNoAvailability',
      'isPartnerLoading',
      'isQuoteLoading',
    ]),

    ...mapGetters('authModal', ['willAuthPromise']),

    getSetTimezone: {
      get() {
        return this.getInputs.timezone;
      },
      set(value) {
        this.$store.commit('quote/setInputsTimezone', value);
      },
    },

    hasCheckoutListener() {
      return this.$listeners?.checkout;
    },

    hasTimeslots() {
      return size(this.timeslots);
    },

    timeslots() {
      if (this.hasNoAvailability) {
        return [];
      }

      if (this.showMoreTimeslots) {
        return this.getAvailability;
      }

      return take(this.getAvailability, 4);
    },

    morningTimeslots() {
      return [...this.getAvailability].filter((timeslot) => {
        const time = DateTime.fromISO(timeslot.start_at, {
          zone: this.timezone,
        });

        return time.hour < 12;
      });
    },

    afternoonTimeslots() {
      return [...this.getAvailability].filter((timeslot) => {
        const time = DateTime.fromISO(timeslot.start_at, {
          zone: this.timezone,
        });

        return time.hour >= 12 && time.hour < 17;
      });
    },

    eveningTimeslots() {
      return [...this.getAvailability].filter((timeslot) => {
        const time = DateTime.fromISO(timeslot.start_at, {
          zone: this.timezone,
        });

        return time.hour >= 17;
      });
    },

    formalName() {
      return new User(this.partner).formalName();
    },
  },

  watch: {
    isQuoteLoading(value) {
      if (!value) {
        this.centerSelectedTimeslot();
      }
    },

    partner: {
      async handler(partner) {
        await this.$store.commit('quote/setPartner', partner);
        await this.$store.dispatch('quote/fetchAvailabilityAndUseExistingTimeslot');
      },
      deep: true,
    },
  },

  async created() {
    const timezone = this.defaultTimezone
      ? this.defaultTimezone
      : DateTime.local().zoneName;

    // This is used to prepend the timezone list with the user defined or machine timezone (not always in the options we have)
    // and needs to be separate from the `this.inputs.timezone` which changes based on selection
    this.$store.commit('quote/setTimezone', timezone);

    this.$store.commit('quote/setPartner', this.partner);

    this.$store.commit(
      'quote/setInputsDate',
      this.validDateFrom(this.defaultDate)
      || this.validDateFrom(get(parsedLocalStorage('quoteParams'), 'date', '')),
    );

    this.$store.commit(
      'quote/setInputsTimeslot',
      size(this.defaultTimeslot)
        ? this.defaultTimeslot
        : get(parsedLocalStorage('quoteParams'), 'timeslot', ''),
    );

    this.$store.commit(
      'quote/setInputsTimezone',
      this.defaultTimezone
      || get(
        parsedLocalStorage('quoteParams'),
        'timezone',
        DateTime.local().zoneName,
      ),
    );

    await this.fetchVcprLocation();

    if (this.getInputs.date) {
      // This will also call setDate() -> fetchAvailability() if page is not ready
      await this.$store.dispatch('quote/setDateOnQuote');
    }

    this.centerSelectedTimeslot();

    this.$store.commit('quote/setPageReady', true);
  },

  methods: {
    ...mapActions('authModal', [
      'setAuthModalMode',
      'setIsBooking',
    ]),

    ...mapActions('quote', ['fetchVcprLocation']),

    calculateSelectedTimeslotPosition(container, element) {
      const elementCenterY = element.offsetTop + (element.clientHeight / 2);
      const halfContainerHeight = container.clientHeight / 2;

      if (elementCenterY > halfContainerHeight) {
        return elementCenterY - halfContainerHeight;
      }

      return 0;
    },

    centerSelectedTimeslot() {
      const el = document.querySelector('[data-selected-timeslot]');
      const container = this.$refs.timeslotsContainer;

      if (!el || !container) {
        return;
      }

      container.scrollTo({
        top: this.calculateSelectedTimeslotPosition(container, el),
        behavior: 'smooth',
      });
    },

    async checkout() {
      if (this.hasQuote && await this.registerGuests()) {
        this.clearPromoCodesSession();
        if (this.hasCheckoutListener) {
          this.$emit('checkout');
        } else {
          this.$analytics.track({
            eventName: 'continue_booking_submit',
            parent: 'guided-search',
            child: 'select-booking',
            event_status: 'SUCCESS',
            version: '2.0',
          });

          window.location.href = this.getBookingUrl(this.getQuote);
        }
      }
    },

    clearPromoCodesSession() {
      sessionStorage.removeItem('promoCodes');
    },

    getAssetUrl,

    async registerGuests() {
      return !!(this.hasUser || await this.registerNewAccount());
    },

    registerNewAccount() {
      this.setAuthModalMode('register');
      this.setIsBooking(true);

      return this.willAuthPromise;
    },
  },
};
</script>
